// client/src/components/tendermaker/ConsolidationPanel.js
import React, { useMemo, useState } from "react";

const btn = {
  padding: "7px 14px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 12,
};
const btn2 = { ...btn, background: "#fafafa" };
const btnPrimary = { ...btn, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };
const btnDanger = { ...btn, color: "#dc2626", borderColor: "#fca5a5" };
const tiny = { color: "#6b7280", fontSize: 10 };

function isPdfName(name = "") {
  return String(name).split(".").pop()?.toLowerCase() === "pdf";
}

export default function ConsolidationPanel({
  reqs,
  items,
  getItem,
  onMergeKeys,
  onRenderAndMerge,
  onDownloadFile,
  onClose,
  onSetErr,
  busy,
}) {
  // Track selected files by a composite key: `${itemId}::${fileId || key}`
  const [selectedFiles, setSelectedFiles] = useState({});
  // Track which requirements are expanded
  const [expanded, setExpanded] = useState({});

  // Build structured list: each requirement with its individual files + draft info
  const consolidatable = useMemo(() => {
    return reqs
      .map((r) => {
        const it = getItem(r.itemId);
        if (!it || it.removed) return null;
        const rawFiles = Array.isArray(it.files) ? it.files : [];
        const hasDraft = it.sourceType === "draft" && it.sourceRef?.draftId;
        const hasSourcePdf = it.sourceRef?.key && isPdfName(it.sourceRef?.fileName || "");

        // Build individual file entries
        const fileEntries = [];

        // Legacy sourceRef PDF (if not already in files[])
        if (hasSourcePdf) {
          const alreadyInFiles = rawFiles.some((f) => f.key === it.sourceRef.key);
          if (!alreadyInFiles) {
            fileEntries.push({
              id: `src_${it.sourceRef.key}`,
              fileName: it.sourceRef.fileName || "source.pdf",
              key: it.sourceRef.key,
              type: "source",
              isPdf: true,
            });
          }
        }

        // files[] entries
        rawFiles.forEach((f, idx) => {
          const fId = f.fileId || f.key || `f${idx}`;
          fileEntries.push({
            id: fId,
            fileName: f.fileName || fId,
            key: f.key || "",
            type: f.type || "file",
            isPdf: isPdfName(f.fileName || ""),
            originalFile: f,
          });
        });

        // Draft entry (renders to PDF on merge)
        if (hasDraft) {
          fileEntries.push({
            id: `draft_${it.sourceRef.draftId}`,
            fileName: `Draft: ${r.label}`,
            key: null,
            type: "draft",
            isPdf: false,
            draftId: it.sourceRef.draftId,
          });
        }

        if (fileEntries.length === 0) return null;

        return {
          itemId: r.itemId,
          label: r.label,
          type: r.type,
          fileEntries,
          pdfCount: fileEntries.filter((f) => f.isPdf).length,
          draftCount: fileEntries.filter((f) => f.type === "draft").length,
        };
      })
      .filter(Boolean);
  }, [reqs, getItem]);

  // Auto-expand all on first render
  useMemo(() => {
    const exp = {};
    consolidatable.forEach((c) => (exp[c.itemId] = true));
    setExpanded(exp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consolidatable.length]);

  function fileKey(itemId, fileEntry) {
    return `${itemId}::${fileEntry.id}`;
  }

  function toggleFile(itemId, entry) {
    const k = fileKey(itemId, entry);
    setSelectedFiles((prev) => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = { ...entry, itemId };
      return next;
    });
  }

  function toggleRequirement(itemId) {
    const c = consolidatable.find((x) => x.itemId === itemId);
    if (!c) return;
    const allKeys = c.fileEntries.map((f) => fileKey(itemId, f));
    const allSelected = allKeys.every((k) => selectedFiles[k]);
    setSelectedFiles((prev) => {
      const next = { ...prev };
      if (allSelected) {
        allKeys.forEach((k) => delete next[k]);
      } else {
        c.fileEntries.forEach((f) => {
          next[fileKey(itemId, f)] = { ...f, itemId };
        });
      }
      return next;
    });
  }

  function toggleExpand(itemId) {
    setExpanded((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  }

  function selectAllFiles() {
    const next = {};
    consolidatable.forEach((c) => {
      c.fileEntries.forEach((f) => {
        next[fileKey(c.itemId, f)] = { ...f, itemId: c.itemId };
      });
    });
    setSelectedFiles(next);
  }

  function selectAllPdfs() {
    const next = {};
    consolidatable.forEach((c) => {
      c.fileEntries.forEach((f) => {
        if (f.isPdf) next[fileKey(c.itemId, f)] = { ...f, itemId: c.itemId };
      });
    });
    setSelectedFiles(next);
  }

  function selectNone() {
    setSelectedFiles({});
  }

  // Gather selected into merge-ready data
  const selectedEntries = Object.values(selectedFiles);
  const selectedPdfKeys = selectedEntries.filter((e) => e.isPdf && e.key).map((e) => e.key);
  const selectedDraftIds = selectedEntries.filter((e) => e.type === "draft" && e.draftId).map((e) => e.draftId);
  const totalMergeable = selectedPdfKeys.length + selectedDraftIds.length;

  function handleMergeSelected() {
    if (totalMergeable < 1) {
      return onSetErr("Select at least one PDF or draft to merge.");
    }
    if (selectedDraftIds.length > 0) {
      onRenderAndMerge(selectedPdfKeys, selectedDraftIds);
    } else if (selectedPdfKeys.length >= 2) {
      onMergeKeys(selectedPdfKeys);
    } else if (selectedPdfKeys.length === 1) {
      // Single PDF — just download it
      onRenderAndMerge(selectedPdfKeys, []);
    } else {
      onSetErr("Select at least one file to merge.");
    }
  }

  function handleDownloadSeparately() {
    for (const entry of selectedEntries) {
      if (entry.key && entry.originalFile) {
        onDownloadFile(entry.itemId, entry.originalFile);
      } else if (entry.key) {
        onDownloadFile(entry.itemId, { key: entry.key, fileName: entry.fileName });
      }
    }
  }

  const totalFiles = consolidatable.reduce((s, c) => s + c.fileEntries.length, 0);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 20,
          width: "90%",
          maxWidth: 750,
          maxHeight: "85vh",
          overflow: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontWeight: 900, fontSize: 16 }}>Consolidate / Merge Documents</div>
          <button style={btnDanger} onClick={onClose}>Close</button>
        </div>

        <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>
          Select individual files from each requirement to include in the final merged document.
          Drafts will be rendered to PDF before merging. Removing files here does not affect the requirement.
        </div>

        {/* Selection controls */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button style={btn2} onClick={selectAllFiles}>All Files</button>
          <button style={btn2} onClick={selectAllPdfs}>PDFs Only</button>
          <button style={btn2} onClick={selectNone}>Deselect</button>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#6b7280" }}>
            {selectedEntries.length} of {totalFiles} files selected
            {totalMergeable > 0 && ` (${totalMergeable} mergeable)`}
          </span>
        </div>

        {/* Requirements + individual files */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {consolidatable.length === 0 && (
            <div style={{ color: "#9ca3af", fontSize: 12, padding: 12 }}>
              No requirements with attached files or drafts found.
            </div>
          )}
          {consolidatable.map((c) => {
            const reqFileKeys = c.fileEntries.map((f) => fileKey(c.itemId, f));
            const allChecked = reqFileKeys.every((k) => selectedFiles[k]);
            const someChecked = reqFileKeys.some((k) => selectedFiles[k]);
            const isExpanded = expanded[c.itemId];

            return (
              <div
                key={c.itemId}
                style={{
                  borderRadius: 10,
                  border: someChecked ? "1px solid #93c5fd" : "1px solid #e5e7eb",
                  background: someChecked ? "#f0f9ff" : "#fafafa",
                  overflow: "hidden",
                }}
              >
                {/* Requirement header row */}
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    padding: "8px 12px",
                    cursor: "pointer",
                    background: someChecked ? "#eff6ff" : "#fafafa",
                  }}
                  onClick={() => toggleExpand(c.itemId)}
                >
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => { e.stopPropagation(); toggleRequirement(c.itemId); }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 12, color: "#6b7280" }}>
                    {isExpanded ? "\u25BC" : "\u25B6"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{c.label}</div>
                    <div style={tiny}>
                      <span style={{
                        display: "inline-block",
                        padding: "1px 5px",
                        borderRadius: 4,
                        background: c.type === "annexure" ? "#dbeafe" : c.type === "custom" ? "#f3e8ff" : "#f0fdf4",
                        fontWeight: 700,
                        marginRight: 4,
                      }}>
                        {c.type}
                      </span>
                      {c.fileEntries.length} file{c.fileEntries.length > 1 ? "s" : ""}
                      {c.pdfCount > 0 && ` (${c.pdfCount} PDF)`}
                      {c.draftCount > 0 && " + draft"}
                    </div>
                  </div>
                </div>

                {/* Expanded: individual files */}
                {isExpanded && (
                  <div style={{ padding: "0 12px 8px 36px", display: "flex", flexDirection: "column", gap: 3 }}>
                    {c.fileEntries.map((f) => {
                      const k = fileKey(c.itemId, f);
                      const isChecked = !!selectedFiles[k];
                      const badgeColor =
                        f.isPdf ? "#fee2e2" :
                        f.type === "draft" ? "#fef3c7" :
                        f.type === "bid_content" ? "#e0e7ff" :
                        "#f3f4f6";
                      const badgeLabel =
                        f.isPdf ? "PDF" :
                        f.type === "draft" ? "DRAFT" :
                        f.type === "bid_content" ? "BID DOC" :
                        f.type === "upload" ? "UPLOAD" :
                        "FILE";

                      return (
                        <label
                          key={f.id}
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: isChecked ? "1px solid #93c5fd" : "1px solid #f3f4f6",
                            background: isChecked ? "#eff6ff" : "#fff",
                            cursor: "pointer",
                            fontSize: 11,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleFile(c.itemId, f)}
                            style={{ cursor: "pointer" }}
                          />
                          <span style={{ flex: 1, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {f.fileName}
                          </span>
                          <span style={{
                            padding: "1px 5px",
                            borderRadius: 4,
                            background: badgeColor,
                            fontWeight: 700,
                            fontSize: 9,
                            flexShrink: 0,
                          }}>
                            {badgeLabel}
                          </span>
                          {!f.isPdf && f.type !== "draft" && (
                            <span style={{ ...tiny, fontStyle: "italic", flexShrink: 0 }}>
                              (not mergeable)
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button
            style={btnPrimary}
            onClick={handleMergeSelected}
            disabled={busy || totalMergeable < 1}
          >
            {busy ? "Merging..." : `Merge Selected (${totalMergeable})`}
          </button>
          <button
            style={btn2}
            onClick={handleDownloadSeparately}
            disabled={!selectedEntries.filter((e) => e.key).length}
          >
            Download Separately ({selectedEntries.filter((e) => e.key).length})
          </button>
          <div style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af" }}>
            Only PDFs and rendered drafts can be merged. Other files can be downloaded separately.
          </div>
        </div>
      </div>
    </div>
  );
}
